import { Component, Input, OnInit, inject } from '@angular/core';
import { base64ToFile2, readFile } from '@functions/files';
import { first, forEach } from 'lodash';
import { CommonComponent } from '@components/abstract/common-component.component';
import { CommonVerbsApiService } from '@services/common/common-verbs-api.service';
import { Community } from '@models/communities/community.model';
import { ControlValueAccessor } from '@angular/forms';
import { CoreMediaService } from '@services/common/media.service';
import { CropperComponent } from '@ui/common/cropper/cropper.component';
import { LibraryComponent } from '@ui/common/library/library.component';
import { Media } from '@models/media/media.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';
import { Size } from '@models/common/size.model';
import { ToastrService } from 'ngx-toastr';

@Component({
  template: '',
})
export abstract class DropZoneInputComponent
  extends CommonComponent
  implements ControlValueAccessor
{
  private api = inject(CommonVerbsApiService);
  private toastr = inject(ToastrService);
  private modal = inject(NgbModal);
  private service = inject(CoreMediaService);
  @Input() community: Community | null = null;
  @Input() source = 'UNKNOWN';

  @Input() accept = 'image/*';
  @Input() label = 'JPG or PNG';
  @Input() size: Size | null = null;

  media: Media | null;

  value: any | null = null;
  private onTouched!: any;
  private onChanged!: any;

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    this.value = value;
    if (value) {
      this.api.get<Media>(`media/${value}`).subscribe(response => {
        this.media = response;
      });
    }
  }

  prepare(event: NgxDropzoneChangeEvent) {
    const file = event.addedFiles[0];
    if (file) {
      readFile(file).then(() => {
        if (this.size !== null) {
          this.cropper(file);
        } else {
          this.upload(file);
        }
      });
    }
  }

  select(media: Media) {
    this.media = media;
    this.value = media.id;
    this.onChanged(media.id);
    this.onTouched();
  }

  library() {
    const modal = this.modal.open(LibraryComponent, {
      size: 'lg',
      centered: true,
    });
    modal.componentInstance.community = this.community;
    modal.componentInstance.emitter.subscribe(async (media: Media) => {
      if (media) {
        if (
          media.width === this.size?.width &&
          media.height === this.size?.height
        ) {
          this.select(media);
        } else {
          const base = await this.service.getBase64Image(`${media.url}`);
          if (base) {
            base64ToFile2(`${base}`, `${media.name}`, 'image/jpg').then(
              file => {
                if (file) {
                  this.cropper(file);
                }
              }
            );
          }
        }
      }
      this.modal.dismissAll();
    });
  }

  cropper(file: File) {
    const modal = this.modal.open(CropperComponent, {
      size: 'lg',
      centered: true,
    });
    modal.componentInstance.file = file;
    modal.componentInstance.size = this.size;
    modal.componentInstance.emitter.subscribe((cropped: File) => {
      this.upload(cropped);
      this.modal.dismissAll();
    });
  }

  upload(file: File) {
    const params = {
      file: file,
      communityId: this.community?.id,
      source: this.source,
    };
    const subscribe = this.api.form<Media[]>(`media/upload`, params).subscribe({
      error: err => this.toastr.error(err?.message || 'An error occurred.'),
      next: response => {
        if (response) {
          const media = first(response);
          if (media) {
            this.select(media);
          }
        }
      },
    });
    this.unsubscribe.push(subscribe);
  }
}
